import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  PASSWORD_MAX_CHARS,
  PASSWORD_MIN_CHARS,
  PASSWORD_RULES,
  completeActivation,
  inspectActivation,
  passwordLength,
  takeActivationToken,
  type LinkPurpose,
} from '../../services/activation';

// Account activation (/activate#<token>): a neutral RadioOS screen where an invited user chooses
// the first password. It never signs the user in. Rendered outside the authenticated application.

export type ActivationStage =
  | { kind: 'checking' }
  | { kind: 'invalid' }
  | { kind: 'rate_limited' }
  | { kind: 'unavailable' }
  | { kind: 'form'; error?: string; busy?: boolean }
  | { kind: 'done'; next: 'sign_in' | 'mfa_required' };

export const MFA_REQUIRED_MESSAGE =
  'Your password has been created. When you sign in, you will set up an authenticator app (multi-factor authentication) before you can continue.';
export const SIGN_IN_MESSAGE = 'Your password has been created. You can now sign in with your e-mail address and the new password.';
export const INVALID_MESSAGE = 'This activation link is invalid or has expired. Ask your administrator for a new link.';
export const RESET_INVALID_MESSAGE = 'This password-reset link is invalid or has expired. Ask your administrator for a new link.';
export const RESET_DONE_MESSAGE =
  'Your password has been changed and every earlier session has been signed out. Sign in with the new password; your authenticator app is still required.';

const field = 'w-full bg-gray-900 border border-gray-600 rounded px-3 py-2 text-sm';

export function ActivationView({ stage, password, confirm, show, onPassword, onConfirm, onToggleShow, onSubmit, purpose = 'activation' }: {
  purpose?: LinkPurpose;
  stage: ActivationStage;
  password: string;
  confirm: string;
  show: boolean;
  onPassword: (v: string) => void;
  onConfirm: (v: string) => void;
  onToggleShow: () => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const length = passwordLength(password);
  const mismatch = confirm !== '' && confirm !== password;
  const ready = length >= PASSWORD_MIN_CHARS && length <= PASSWORD_MAX_CHARS && confirm === password;
  return (
    <main className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-800 border border-gray-700 rounded-lg p-8 space-y-5">
        <div className="text-center">
          <img src="/logo.png" alt="" className="h-12 w-auto mx-auto" />
          <h1 className="text-2xl font-bold mt-3">{purpose === 'reset' ? 'Choose a new RadioOS password' : 'Activate your RadioOS account'}</h1>
        </div>
        {stage.kind === 'checking' && <p role="status" className="text-sm text-gray-300 text-center">Checking the activation link…</p>}
        {stage.kind === 'invalid' && <p role="alert" className="text-sm text-yellow-200 bg-yellow-900/40 border border-yellow-700 rounded p-3">{purpose === 'reset' ? RESET_INVALID_MESSAGE : INVALID_MESSAGE}</p>}
        {stage.kind === 'rate_limited' && <p role="alert" className="text-sm text-yellow-200 bg-yellow-900/40 border border-yellow-700 rounded p-3">Too many attempts. Please try again later.</p>}
        {stage.kind === 'unavailable' && <p role="alert" className="text-sm text-red-200">The service could not be reached. Please try again later.</p>}
        {stage.kind === 'done' && (
          <div role="status" className="text-sm bg-green-900/40 border border-green-700 rounded p-3 space-y-2">
            <p>{purpose === 'reset' ? RESET_DONE_MESSAGE : stage.next === 'mfa_required' ? MFA_REQUIRED_MESSAGE : SIGN_IN_MESSAGE}</p>
            <p><a className="text-blue-300 underline" href="/">Go to sign-in</a></p>
          </div>
        )}
        {stage.kind === 'form' && (
          <form className="space-y-4" onSubmit={onSubmit} noValidate>
            <p className="text-sm text-gray-300">Choose the password for your account. Nobody else, including your administrator, will know it.</p>
            <ul aria-label="Password requirements" className="text-xs text-gray-300 list-disc pl-5 space-y-1">
              {PASSWORD_RULES.map((r) => <li key={r}>{r}</li>)}
            </ul>
            <div>
              <label htmlFor="activation-password" className="block text-xs text-gray-300 mb-1">New password</label>
              <input id="activation-password" name="new-password" type={show ? 'text' : 'password'} autoComplete="new-password"
                className={field} value={password} maxLength={1024} onChange={(e) => onPassword(e.target.value)}
                aria-describedby="activation-length" />
              <p id="activation-length" aria-live="polite" className={`text-xs mt-1 ${length > 0 && length < PASSWORD_MIN_CHARS ? 'text-yellow-200' : 'text-gray-400'}`}>
                {length} of at least {PASSWORD_MIN_CHARS} characters
              </p>
            </div>
            <div>
              <label htmlFor="activation-confirm" className="block text-xs text-gray-300 mb-1">Confirm password</label>
              <input id="activation-confirm" name="confirm-password" type={show ? 'text' : 'password'} autoComplete="new-password"
                className={field} value={confirm} maxLength={1024} onChange={(e) => onConfirm(e.target.value)}
                aria-invalid={mismatch ? true : undefined} aria-describedby={mismatch ? 'activation-mismatch' : undefined} />
              {mismatch && <p id="activation-mismatch" className="text-xs text-red-300 mt-1">The passwords do not match.</p>}
            </div>
            <button type="button" className="text-xs text-blue-300 underline" aria-pressed={show} onClick={onToggleShow}>
              {show ? 'Hide password' : 'Show password'}
            </button>
            {stage.error && <p role="alert" className="text-sm text-red-200 bg-red-900/40 border border-red-700 rounded p-3">{stage.error}</p>}
            <button type="submit" disabled={!ready || stage.busy}
              className="w-full px-4 py-2.5 rounded bg-blue-600 hover:bg-blue-500 font-semibold disabled:opacity-40">
              {stage.busy ? 'Saving…' : 'Set password'}
            </button>
          </form>
        )}
      </div>
    </main>
  );
}

export default function ActivationPage({ purpose = 'activation' }: { purpose?: LinkPurpose }) {
  // The token lives only in this ref (memory); it is taken out of the address bar immediately.
  const token = useRef<string | null>(null);
  if (token.current === null) token.current = takeActivationToken(window.location, window.history);
  const [stage, setStage] = useState<ActivationStage>({ kind: 'checking' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow] = useState(false);

  useEffect(() => {
    let live = true;
    inspectActivation(token.current || '', undefined, purpose).then((r) => {
      if (live) setStage(r === 'valid' ? { kind: 'form' } : { kind: r });
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (stage.kind !== 'form' || stage.busy || confirm !== password) return;
    setStage({ kind: 'form', busy: true });
    const r = await completeActivation(token.current || '', password, undefined, purpose);
    if (r.kind === 'activated') {
      token.current = '';
      setPassword('');
      setConfirm('');
      setStage({ kind: 'done', next: r.next });
    } else if (r.kind === 'policy') {
      setStage({ kind: 'form', error: r.message });
    } else {
      setStage({ kind: r.kind });
    }
  };

  return (
    <ActivationView purpose={purpose} stage={stage} password={password} confirm={confirm} show={show}
      onPassword={setPassword} onConfirm={setConfirm} onToggleShow={() => setShow((s) => !s)} onSubmit={submit} />
  );
}
